import {
  Box,
  type CSSObject,
  Drawer,
  List,
  Stack,
  styled,
  type Theme,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { memo } from 'react';
import { useSideMenu } from './useSideMenu';
import GarageOutlinedIcon from '@mui/icons-material/GarageOutlined';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import SideMenuItem from './SideMenuItem';
import { useTranslation } from 'react-i18next';

const DRAWER_EXPAND_WIDTH = 304;
const DRAWER_SHRINK_WIDTH = 96;

const openedMixin = (theme: Theme): CSSObject => ({
  width: DRAWER_EXPAND_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  '& .MuiStack-root': {
    transition: theme.transitions.create('padding', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  overflowX: 'hidden',
  '& .drawer-toggle-icon': {
    transform: 'rotate(180deg)',
  },
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: DRAWER_SHRINK_WIDTH,
  '& .MuiStack-root': {
    transition: theme.transitions.create('padding', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  '& .drawer-toggle-icon': {
    position: 'absolute',
    visibility: 'hidden',
    padding: '0px 4px 0px 4px',
  },
  '&:hover': {
    '& .side-menu-logo': {
      visibility: 'hidden',
    },
    '& .drawer-toggle-icon': {
      visibility: 'visible',
    },
  },
});

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme }) => ({
  width: DRAWER_EXPAND_WIDTH,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  '& .drawer-toggle-icon': {
    cursor: 'pointer',
  },
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
      },
    },
  ],
}));

const SideMenu = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const { open, vehiclesSearchParams, transferSearchParams, handleClickOpen, handleClose } =
    useSideMenu();

  return (
    <Box>
      {isMobile ? (
        // モバイルレイアウトのサイドメニュー
        <Drawer
          open={open}
          variant="temporary"
          sx={{
            '& .MuiDrawer-paper': { width: DRAWER_EXPAND_WIDTH },
          }}
          onClose={handleClose}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="start"
            pl={3.5}
            pt={3}
            pb={4}
            sx={{ minHeight: 86, boxSizing: 'border-box' }}
          >
            <Stack position="relative" direction="row" alignItems="center" gap={1.5} height={32}>
              <Box className="side-menu-logo" style={{ height: 23 }}>
                <img src="/logo.svg" alt="Vehicle Console" />
              </Box>
              {open && <img src="/logo_txt.svg" alt="Vehicle Console" />}
            </Stack>
          </Stack>
          <List>
            <SideMenuItem
              to="/vehicles/"
              searchParams={vehiclesSearchParams}
              icon={<GarageOutlinedIcon fontSize="large" />}
              open={open}
              onClick={handleClose}
            >
              {t('vehicles.title')}
            </SideMenuItem>
            <SideMenuItem
              to="/transfer/"
              searchParams={transferSearchParams}
              icon={<CompareArrowsIcon fontSize="large" />}
              open={open}
              onClick={handleClose}
            >
              {t('transfer.title')}
            </SideMenuItem>
          </List>
        </Drawer>
      ) : (
        // PC レイアウトのサイドメニュー
        <StyledDrawer variant="permanent" open={open} onClose={handleClose}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="start"
            pl={3.5}
            pt={3}
            pb={4}
            sx={{ minHeight: 86, boxSizing: 'border-box' }}
          >
            <Stack position="relative" direction="row" alignItems="center" gap={1.5} height={32}>
              <Box className="side-menu-logo" onClick={handleClickOpen} style={{ height: 23 }}>
                <img src="/logo.svg" alt="Vehicle Console" />
              </Box>
              {open && <img src="/logo_txt.svg" alt="Vehicle Console" />}
              <Box
                onClick={open ? handleClose : handleClickOpen}
                className="drawer-toggle-icon"
                style={{ height: 32 }}
              >
                <img src="/icon_button_outlined.svg" alt="サイドメニュー開閉" />
              </Box>
            </Stack>
          </Stack>
          <List>
            <SideMenuItem
              to="/vehicles/"
              searchParams={vehiclesSearchParams}
              icon={<GarageOutlinedIcon fontSize="large" />}
              open={open}
            >
              {t('vehicles.title')}
            </SideMenuItem>
            <SideMenuItem
              to="/transfer/"
              searchParams={transferSearchParams}
              icon={<CompareArrowsIcon fontSize="large" />}
              open={open}
            >
              {t('transfer.title')}
            </SideMenuItem>
          </List>
        </StyledDrawer>
      )}
    </Box>
  );
};

export default memo(SideMenu);
